import type { DataLayerEvent } from '@marriott/mi-headless-utils';
import { ConsentOptionPayload } from '../../organisms/WebPromptConsent/WebPromptConsent.types';
import { pageViewACDLkeyGenerator, webPromptConsentACDLkeyGenerator } from './acdlKeyGenerator';
import { WEB_PROMPT_EXPERIENCE_VARIANTS } from './constants/constants';

// Utility to map raw consent option to ConsentOptionPayload
type RawConsentOption = {
  method?: { code?: string } | string;
  type?: { code?: string } | string;
  subscribed?: boolean;
};

export function mapToConsentOptionPayload(opt: RawConsentOption): ConsentOptionPayload {
  return {
    method: { code: typeof opt.method === 'object' ? opt.method?.code || '' : (opt.method as string) || '' },
    type: { code: typeof opt.type === 'object' ? opt.type?.code || '' : (opt.type as string) || '' },
    subscribed: !!opt.subscribed,
  };
}

/**
 * Helper to push ACCOUNT_UPDATE datalayer event for WebPrompt flows.
 * @param updateDatalayer - The updateDatalayer function from useSession
 * @param options - Consent options array
 * @param phoneExists - 'Y' or 'N' for phone existence
 */

type dataLayerType = Record<string, unknown>;

export interface PushWebPromptDatalayerArgs {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  datalayer?: dataLayerType;
  updateDatalayer: (payload: DataLayerEvent) => void;
  options?: ConsentOptionPayload[];
  phoneExists: 'Y' | 'N' | 'E';
  eventName: string;
  modal?: boolean;
  isError?: boolean;
  isMobileUpdate?: boolean;
  isConsentUpdate?: boolean;
}

/**
 * Generic helper to push datalayer event for WebPrompt flows.
 * Includes user.settings_preferences only for ACCOUNT_UPDATE events.
 */

export function pushWebPromptDatalayer(args: PushWebPromptDatalayerArgs) {
  const {
    datalayer = {},
    updateDatalayer,
    options = [],
    phoneExists,
    eventName,
    modal = true,
    isError,
    isMobileUpdate = false,
    isConsentUpdate = false,
  } = args;
  const { emailConsent, smsConsent, postalConsent } = getConsentStatusFromOptions(options, isError);

  const allSubscribed =
    [
      ['EML', 'PRGRM'],
      ['EML', 'MRKT'],
    ].some(([method, type]) =>
      options?.some(
        option => option?.method?.code === method && option?.type?.code === type && option?.subscribed === true
      )
    ) &&
    [['SMS', 'MRKT']].every(([method, type]) =>
      options?.some(
        option => option?.method?.code === method && option?.type?.code === type && option?.subscribed === true
      )
    ) &&
    [['POST', 'GNRL']].every(([method, type]) =>
      options?.some(
        option => option?.method?.code === method && option?.type?.code === type && option?.subscribed === true
      )
    );

  const consentString = getConsentDataLayerString({
    phoneExists,
    emailConsent,
    smsConsent,
    postalConsent,
  });
  const experience_account_update = isMobileUpdate
    ? WEB_PROMPT_EXPERIENCE_VARIANTS.VARIANT_A
    : isConsentUpdate && allSubscribed
    ? WEB_PROMPT_EXPERIENCE_VARIANTS.VARIANT_B
    : getExperienceVariant(phoneExists, emailConsent, smsConsent, postalConsent);

  const experience = getExperienceVariant(phoneExists, emailConsent, smsConsent, postalConsent);

  let datalayerObj: DataLayerEvent = {
    event: eventName,
    ...pageViewACDLkeyGenerator({ datalayer, isModel: modal, experience }),
  };

  if (eventName === 'ACCOUNT_UPDATE') {
    datalayerObj = {
      event: eventName,
      ...webPromptConsentACDLkeyGenerator(consentString, experience_account_update, modal),
    };
  }
  updateDatalayer(datalayerObj);
}

export function getExperienceVariant(
  phoneExists: string,
  emailConsent: string,
  smsConsent: string,
  postalConsent: string
) {
  const phone = phoneExists === 'Y';
  const consents = [emailConsent, smsConsent, postalConsent];
  const allConsentsY = consents.every(c => c === 'Y');
  const hasConsentIssues = consents.some(c => c === 'N' || c === 'E');

  if (!phone && hasConsentIssues) return WEB_PROMPT_EXPERIENCE_VARIANTS.VARIANT_A;
  if (phone && hasConsentIssues) return WEB_PROMPT_EXPERIENCE_VARIANTS.VARIANT_B;
  if (!phone && allConsentsY) return WEB_PROMPT_EXPERIENCE_VARIANTS.VARIANT_C;
  if (phone && allConsentsY) return WEB_PROMPT_EXPERIENCE_VARIANTS.VARIANT_D;

  return '';
}

// Utility to generate consent datalayer string for WebPrompt flows

type ConsentStatus = {
  phoneExists: 'Y' | 'N' | 'E';
  emailConsent: 'Y' | 'N' | 'E';
  smsConsent: 'Y' | 'N' | 'E';
  postalConsent: 'Y' | 'N' | 'E';
};

export function getConsentDataLayerString({
  phoneExists,
  emailConsent,
  smsConsent,
  postalConsent,
}: ConsentStatus): string {
  return `Phone Exists:${phoneExists}|Email Consent:${emailConsent}|SMS Consent:${smsConsent}|Postal Consent:${postalConsent}`;
}

export function getConsentStatusFromOptions(
  communicationOptions: ConsentOptionPayload[] = [],
  isError: boolean = false
): Omit<ConsentStatus, 'phoneExists'> {
  let emailConsent: 'Y' | 'N' | 'E' = 'N';
  let smsConsent: 'Y' | 'N' | 'E' = 'N';
  let postalConsent: 'Y' | 'N' | 'E' = 'N';

  const resolveConsent = (subscribed?: boolean, isSelected?: boolean): 'Y' | 'N' | 'E' => {
    if (!subscribed && !isSelected) return 'N';
    return isError && isSelected && !subscribed ? 'E' : 'Y';
  };

  communicationOptions.forEach(opt => {
    if (
      (opt.method?.code === 'EML' && opt.type?.code === 'PRGRM') ||
      (opt.method?.code === 'EMAIL' && opt.type?.code === 'MRKT')
    ) {
      emailConsent = resolveConsent(opt.subscribed, opt?.isSelected);
    }

    if (opt.method?.code === 'SMS' && opt.type?.code === 'MRKT') {
      smsConsent = resolveConsent(opt.subscribed, opt?.isSelected);
    }

    if (opt.method?.code === 'POST' && opt.type?.code === 'GNRL') {
      postalConsent = resolveConsent(opt.subscribed, opt?.isSelected);
    }
  });

  return { emailConsent, smsConsent, postalConsent };
}

// Helper to determine if phone exists (type MT only)
interface Customer {
  contactInformation?: {
    phones?: Array<{ number?: string; type?: { code?: string } }>;
  };
}

export function getPhoneExistsFromCustomer(customer: Customer): 'Y' | 'N' {
  const phones = customer?.contactInformation?.phones || [];
  return phones.some(phone => !!phone?.number && phone?.type?.code === 'MT') ? 'Y' : 'N';
}
